import { colors } from '@aloft/toolkit-sui';

import {
  Bar,
  CartesianGrid,
  ComposedChart,
  DefaultLegendContent,
  DefaultLegendContentProps,
  Legend,
  ReferenceArea,
  ReferenceLine,
} from 'recharts';

import { selectSiteCostPerSf } from '../../../redux/features/selectors/selectSiteCostPerSf.ts';
import { useAppSelector } from '../../../redux/hooks.ts';
import { GraphTitle } from '../components/GraphTitle.tsx';
import { GraphXAxis } from '../components/GraphXAxis.tsx';
import { GraphYAxis } from '../components/GraphYAxis.tsx';
import { ResponsiveGraphWrapper } from '../components/ResponsiveGraphWrapper.tsx';
import { SimpleGraphTooltip } from '../components/SimpleGraphTooltip.tsx';
import { GroupedVacantLandSalesChartData, VacantLandSalesGraphProps } from './types.ts';
import { groupVacantLandSalesData } from './utils.ts';

const LegendContent = ({ payload, ...props }: DefaultLegendContentProps) => (
  <DefaultLegendContent
    {...props}
    payload={[
      ...payload!,
      {
        value: 'Subject Property',
        type: 'line',
        color: colors.graphChart4,
      } as const,
      {
        value: '25th-75th Percentile',
        type: 'rect',
        color: colors.graphAreaBackgroundOpaque,
      },
    ]}
  />
);

export const DistributionOfVacantLandGraph = ({
  disableAnimations = false,
  width,
  height,
  vacantLandSalesChartData,
}: VacantLandSalesGraphProps) => {
  const subjectCostPerSF = useAppSelector((state) =>
    selectSiteCostPerSf(state, 'subject'),
  )?.toDollarLike();

  const data = groupVacantLandSalesData(vacantLandSalesChartData);

  const costPerSfs = vacantLandSalesChartData.map((d) => d.costPerSF);
  const minCostPerSF = Math.min(...costPerSfs);
  const maxCostPerSF = Math.max(...costPerSfs);

  const p25CostPerSF = minCostPerSF + (maxCostPerSF - minCostPerSF) * 0.25;
  const p75CostPerSF = minCostPerSF + (maxCostPerSF - minCostPerSF) * 0.75;

  return (
    <ResponsiveGraphWrapper disableAnimations={disableAnimations} width={width} height={height}>
      <ComposedChart
        data={data}
        width={width}
        height={height}
        margin={{ top: 32, left: 8, bottom: 8, right: 8 }}
        style={{ background: colors.graphBackground, borderRadius: 8 }}
      >
        <GraphTitle>Distribution of Vacant Land $ / SF</GraphTitle>
        <CartesianGrid strokeDasharray="3 3" />
        <Legend verticalAlign="top" align="right" content={LegendContent} />
        <SimpleGraphTooltip<GroupedVacantLandSalesChartData>
          disableAnimations={disableAnimations}
          titleKey="costPerSF"
          rows={[
            {
              label: 'Frequency',
              key: 'percentage',
              color: 'graphChart',
              valueFormatter: (v) => `${v?.toLocaleString() ?? '-'}%`,
            },
          ]}
        />
        <GraphXAxis name="Cost Per SF" dataKey="costPerSF" scale="linear" label="$ / SF" />
        <GraphYAxis label="Frequency" tickFormatter={(value) => `${value}%`} />
        <Bar
          name="Vacant Land Sale"
          dataKey="percentage"
          fill={colors.graphChart}
          isAnimationActive={!disableAnimations}
        />

        <ReferenceLine x={subjectCostPerSF} stroke={colors.graphChart4} />
        <ReferenceArea x1={p25CostPerSF} x2={p75CostPerSF} fill={colors.graphAreaBackground} />
      </ComposedChart>
    </ResponsiveGraphWrapper>
  );
};
